import React, { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, FormControlLabel, Paper, Stack, Switch, useTheme } from "@mui/material";
import { QueryInfo, QueryField, socialUniverseField } from "system/query/QueryInfo";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { Agg, getFieldLabelFromAgg } from "system/query/Aggs";
import LeafAggSelector from "components/widgetParts/LeafAggSelector";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import BubbleComparisonPreview from "./BubbleComparisonPreview";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";

export type BubbleComparisonSettingsStateType = {
  customWidgetTitle: string;
  widgetTitle: string;
  widgetTitleAlignment: string;
  xAxisKeywordAsPercent: boolean;
  yAxisKeywordAsPercent: boolean;
  sizeKeywordAsPercent: boolean;
}

export const defaultSettings: BubbleComparisonSettingsStateType = {
  customWidgetTitle: '',
  widgetTitle: 'default',
  widgetTitleAlignment: 'left',
  xAxisKeywordAsPercent: false,
  yAxisKeywordAsPercent: false,
  sizeKeywordAsPercent: false
}

const defaultBubbleComparisonWidgetRequest = {
  name: 'bubble-comparison',
  endpoint: 'query/agg',
  queryParams: {
    include_total: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_MULTI_ALWAYS
};

interface BubbleComparisonAggsInfo extends Agg {
  size?: Agg;
  x?: Agg;
  y?: Agg;
}

export function BuildBubbleComparisonRequest(AggsInfo: BubbleComparisonAggsInfo): WidgetRequest | undefined {
  const widgetRequest : WidgetRequest = { ...defaultBubbleComparisonWidgetRequest };

  const requestInitBody = { aggs: {} };
  if (AggsInfo.size) {
    requestInitBody.aggs = {
      size: {
        op: AggsInfo.size.op,
        field: AggsInfo.size.field,
        filter: AggsInfo.size.filter,
        labels: AggsInfo.size.labels,
        label: AggsInfo.size.label
      }
    };
  }
  if (AggsInfo.x) {
    requestInitBody.aggs = {
      ...requestInitBody.aggs,
      x: {
        op: AggsInfo.x.op,
        field: AggsInfo.x.field,
        filter: AggsInfo.x.filter,
        labels: AggsInfo.x.labels,
        label: AggsInfo.x.label
      }
    };
  }
  if (AggsInfo.y) {
    requestInitBody.aggs = {
      ...requestInitBody.aggs,
      y: {
        op: AggsInfo.y.op,
        field: AggsInfo.y.field,
        filter: AggsInfo.y.filter,
        labels: AggsInfo.y.labels,
        label: AggsInfo.y.label
      }
    };
  }
  widgetRequest.requestInit = { body: requestInitBody };
  return widgetRequest;
}

export function BuildDefaultBubbleComparisonRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const aggsInfo: BubbleComparisonAggsInfo = {};

  // Potential numeric fields for x and y axes, always defaults to average because we don't know the purpose of the field
  const foundNumericFields = queryInfo.fields?.filter((field:QueryField) => {
    return ["integer", "decimal"].includes(field.type);
  }) || false;

  // Potential keyword fields for x and y axes - need options to be useful/consistent
  const foundKeywordFields = queryInfo.fields?.filter((field:QueryField) => {
    return ["keyword", "keywords"].includes(field.type) && field.options?.length;
  }) || false;

  // If we are using the social dataset, default to social_universe - otherwise use records (by supplying no size field)
  const sizeField = queryInfo.fields?.find((field: QueryField) => field.id === socialUniverseField.id) || false;
  if (sizeField) {
    aggsInfo.size = { op: 'social_universe', label: "Social Universe" };
  }

  // If we are using the social dataset, default to sentiment - otherwise find an appropriate numeric or keyword field
  let xField = queryInfo.fields?.find((field: QueryField) => field.id === 'sentiment') || false;
  if (xField) {
    const positiveOption = xField.options?.find((option) => option.id === 'p');
    aggsInfo.x = {
      op: 'keyword',
      field: 'sentiment',
      filter: positiveOption?.id ? [positiveOption?.id] : undefined,
      label: `${xField.label} - ${positiveOption?.label}`
    };
  } else {
    xField = foundNumericFields.pop() || false;
    if (xField) {
      aggsInfo.x = {
        op: 'avg',
        field: xField.id,
        label: xField.label
      };
    } else {
      xField = foundKeywordFields.pop() || false;
      if (xField) {
        const firstOption = xField.options?.[0];
        aggsInfo.x = {
          op: 'keyword',
          field: xField.id,
          filter: firstOption?.id ? [firstOption?.id] : undefined,
          label: `${xField.label} - ${firstOption?.label}`
        };
      }
    }
  }

  // If we are using the social dataset, default to total_engagements - otherwise find an appropriate numeric or keyword field
  let yField = queryInfo.fields?.find((field: QueryField) => field.id === 'total_engagements') || false;
  if (yField) {
    aggsInfo.y = {
      op: 'sum',
      field: 'total_engagements',
      label: yField.label
    };
  } else {
    yField = foundNumericFields.pop() || false;
    if (yField) {
      aggsInfo.y = {
        op: 'avg',
        field: yField.id,
        label: yField.label
      };
    } else {
      yField = foundKeywordFields.pop() || false;
      if (yField) {
        const firstOption = yField.options?.[0];
        aggsInfo.y = {
          op: 'keyword',
          field: yField.id,
          filter: firstOption?.id ? [firstOption?.id] : undefined,
          label: `${yField.label} - ${firstOption?.label}` };
      }
    }
  }

  const widgetRequest : WidgetRequest | undefined = BuildBubbleComparisonRequest(aggsInfo);
  return widgetRequest ? [widgetRequest] : undefined;
}

export default function BubbleComparisonSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const firstActiveRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'bubble-comparison');
  const requestBody = firstActiveRequest?.requestInit?.body || {};

  const [xAggs, setXAggs] = useState<Agg | undefined>(requestBody?.aggs?.x);
  const [yAggs, setYAggs] = useState<Agg | undefined>(requestBody?.aggs?.y);
  const [sizeAggs, setSizeAggs] = useState<Agg | undefined>(requestBody?.aggs?.size);

  const updateAggs = () => {
    if (!props.queryInfo) {
      return;
    }

    const aggsInfo: BubbleComparisonAggsInfo = {};

    aggsInfo.x = xAggs;
    aggsInfo.y = yAggs;
    aggsInfo.size = sizeAggs;

    const widgetRequest : WidgetRequest | undefined = BuildBubbleComparisonRequest(aggsInfo);
    if (!widgetRequest) {
      return;
    }
    props.saveWidgetDataAndRequests({settings: settingsState}, [widgetRequest])
  }

  const updatedDefaultSettings = {...defaultSettings, ...props?.widget?.data?.settings};
  const [settingsState, setSettingsState] = useState<BubbleComparisonSettingsStateType>(updatedDefaultSettings);
  const settingsOnChange = (updatedState: BubbleComparisonSettingsStateType) => setSettingsState(updatedState);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  const makeAggsLabel = (agg: Agg | null): Agg | undefined => {
    if (!agg || !props.queryInfo) {
      return agg || undefined;
    }
    const label = getFieldLabelFromAgg(agg, props.queryInfo, i18n);
    agg.label = label;
    return agg;
  }

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateAggs();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.bubble-comparison.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 &&
          <>
            <Paper sx={{ p: 2, pt: 1, mt: 2, mb: 1 }}>
              <SimpleFormLabel>{i18n.t("x-axis")}</SimpleFormLabel>
              {props.queryInfo && <>
                <LeafAggSelector
                  allowedFieldTypes={["decimal", "integer", "keyword", "keywords"]}
                  queryInfo={props.queryInfo}
                  aggs={xAggs}
                  setAggs={(newAggs: Agg | null) => setXAggs(makeAggsLabel(newAggs))}
                  aggsLabel={"x"}
                  setAggsLabel={() => {}}
                  ></LeafAggSelector>
                {xAggs?.op === 'keyword' && (<>
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                      <Switch
                        checked={settingsState.xAxisKeywordAsPercent}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          const newValue = event.target.checked;
                          setSettingsState((prevState) => ({
                            ...prevState,
                            xAxisKeywordAsPercent: newValue
                          }));
                        }}
                      />
                    }
                    label={i18n.t("widget-types.bubble-comparison.settings.as-percent")}
                  />
                </>)}
              </>}
            </Paper>
            <Paper sx={{ p: 2, pt: 1, mb: 1 }}>
              <SimpleFormLabel>{i18n.t("y-axis")}</SimpleFormLabel>
              {props.queryInfo && <>
                <LeafAggSelector
                  allowedFieldTypes={["decimal", "integer", "keyword", "keywords"]}
                  queryInfo={props.queryInfo}
                  aggs={yAggs}
                  setAggs={(newAggs: Agg | null) => setYAggs(makeAggsLabel(newAggs))}
                  aggsLabel={"y"}
                  setAggsLabel={() => {}}
                  ></LeafAggSelector>
                {yAggs?.op === 'keyword' && (<>
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                      <Switch
                        checked={settingsState.yAxisKeywordAsPercent}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          const newValue = event.target.checked;
                          setSettingsState((prevState) => ({
                            ...prevState,
                            yAxisKeywordAsPercent: newValue
                          }));
                        }}
                      />
                    }
                    label={i18n.t("widget-types.bubble-comparison.settings.as-percent")}
                  />
                </>)}
              </>}
            </Paper>
            <Paper sx={{ p: 2, pt: 1, mb: 1 }}>
              <SimpleFormLabel>{i18n.t("widget-types.bubble-comparison.size")}</SimpleFormLabel>
              {props.queryInfo && <>
                <LeafAggSelector
                  allowedFieldTypes={["decimal", "integer", "keyword", "keywords"]}
                  queryInfo={props.queryInfo}
                  aggs={sizeAggs}
                  setAggs={(newAggs: Agg | null) => setSizeAggs(makeAggsLabel(newAggs))}
                  aggsLabel={"size"}
                  setAggsLabel={() => {}}
                  ></LeafAggSelector>
                {sizeAggs?.op === 'keyword' && (<>
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                      <Switch
                        checked={settingsState.sizeKeywordAsPercent}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          const newValue = event.target.checked;
                          setSettingsState((prevState) => ({
                            ...prevState,
                            sizeKeywordAsPercent: newValue
                          }));
                        }}
                      />
                    }
                    label={i18n.t("widget-types.bubble-comparison.settings.as-percent")}
                  />
                </>)}
              </>}
            </Paper>
          </>}

        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Preview */}
            <BubbleComparisonPreview settingsState={settingsState} />
            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                  {settingsState.widgetTitle === 'custom' &&
                    <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                      <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                      <SettingsTextField
                        keyid="customWidgetTitle"
                        label={i18n.t("widget-settings.custom-text")}
                        value={settingsState.customWidgetTitle}
                      />
                    </Stack>}
                {/* Widget Title Alignment */}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>
            </SettingsOptions>
          </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  )
}
